import type { ComponentRef, Ref, ComponentPropsWithoutRef } from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import { Check, ChevronDown, ChevronUp } from 'lucide-react'
import { cn } from '#utils'

/**
 * Select component
 * 
 * Accessible select/dropdown built on Radix UI primitives with ButterUI styling.
 * Use this for custom-styled select inputs where native <select> is insufficient.
 * 
 * @example
 * ```tsx
 * <Select value={value} onValueChange={setValue}>
 *   <SelectTrigger>
 *     <SelectValue placeholder="Select option..." />
 *   </SelectTrigger>
 *   <SelectContent>
 *     <SelectItem value="option1">Option 1</SelectItem>
 *     <SelectItem value="option2">Option 2</SelectItem>
 *   </SelectContent>
 * </Select>
 * ```
 * 
 * @see https://www.radix-ui.com/primitives/docs/components/select
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/combobox/examples/combobox-select-only/
 */

// Root component
const Select = SelectPrimitive.Root

// Group component
const SelectGroup = SelectPrimitive.Group

// Value component
const SelectValue = SelectPrimitive.Value

/**
 * SelectTrigger - The button that opens the select dropdown
 */
export interface SelectTriggerProps extends ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> {
  /**
   * Whether to show the chevron icon
   * @default true
   */
  showChevron?: boolean
}

function SelectTrigger({
  className,
  children,
  showChevron = true,
  ref,
  ...props
}: SelectTriggerProps & {
  ref?: Ref<ComponentRef<typeof SelectPrimitive.Trigger>>
}) {
  return (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(
        // Base styles
        'flex h-11 w-full items-center justify-between',
        'rounded-14 border border-butter-gray-300 bg-white',
        'px-4 py-2 text-sm',
        // Text styles
        'text-butter-gray-900 placeholder:text-butter-gray-500',
        // Focus styles
        'focus:outline-none focus:ring-2 focus:ring-butter-brand-600 focus:ring-offset-2',
        // Disabled state
        'disabled:cursor-not-allowed disabled:opacity-50',
        // Overflow
        '[&>span]:line-clamp-1',
        // User overrides
        className
      )}
      {...props}
    >
      {children}
      {showChevron && (
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="size-5 text-butter-gray-900" />
        </SelectPrimitive.Icon>
      )}
    </SelectPrimitive.Trigger>
  )
}

/**
 * SelectScrollUpButton - Scroll up indicator
 */
function SelectScrollUpButton({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton> & {
  ref?: Ref<ComponentRef<typeof SelectPrimitive.ScrollUpButton>>
}) {
  return (
    <SelectPrimitive.ScrollUpButton
      ref={ref}
      className={cn(
        'flex cursor-default items-center justify-center py-1',
        className
      )}
      {...props}
    >
      <ChevronUp className="h-4 w-4" />
    </SelectPrimitive.ScrollUpButton>
  )
}

/**
 * SelectScrollDownButton - Scroll down indicator
 */
function SelectScrollDownButton({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton> & {
  ref?: Ref<ComponentRef<typeof SelectPrimitive.ScrollDownButton>>
}) {
  return (
    <SelectPrimitive.ScrollDownButton
      ref={ref}
      className={cn(
        'flex cursor-default items-center justify-center py-1',
        className
      )}
      {...props}
    >
      <ChevronDown className="h-4 w-4" />
    </SelectPrimitive.ScrollDownButton>
  )
}

/**
 * SelectContent - The dropdown content container
 */
export interface SelectContentProps extends ComponentPropsWithoutRef<typeof SelectPrimitive.Content> {
  /**
   * Positioning strategy
   * @default 'popper'
   */
  position?: 'item-aligned' | 'popper'
}

function SelectContent({
  className,
  children,
  position = 'popper',
  ref,
  ...props
}: SelectContentProps & {
  ref?: Ref<ComponentRef<typeof SelectPrimitive.Content>>
}) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          // Base styles
          'relative z-50 max-h-96 min-w-[8rem]',
          'overflow-hidden rounded-10 border border-butter-gray-200',
          'bg-white text-butter-gray-900 shadow-md',
          // Animations
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          'data-[side=bottom]:slide-in-from-top-2',
          'data-[side=left]:slide-in-from-right-2',
          'data-[side=right]:slide-in-from-left-2',
          'data-[side=top]:slide-in-from-bottom-2',
          // Popper positioning
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          // User overrides
          className
        )}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            'p-1',
            position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]'
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
}

/**
 * SelectLabel - Label for a group of items
 */
function SelectLabel({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof SelectPrimitive.Label> & {
  ref?: Ref<ComponentRef<typeof SelectPrimitive.Label>>
}) {
  return (
    <SelectPrimitive.Label
      ref={ref}
      className={cn(
        'py-1.5 pl-8 pr-2 text-sm font-semibold text-butter-gray-900',
        className
      )}
      {...props}
    />
  )
}

/**
 * SelectItem - A single selectable option
 */
function SelectItem({
  className,
  children,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
  ref?: Ref<ComponentRef<typeof SelectPrimitive.Item>>
}) {
  return (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(
        // Base styles
        'relative flex w-full cursor-default select-none items-center',
        'rounded-lg py-1.5 pl-8 pr-2 text-sm',
        'outline-none',
        // Hover/focus state
        'focus:bg-butter-brand-50 focus:text-butter-brand-900',
        // Disabled state
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        // User overrides
        className
      )}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="h-4 w-4" />
        </SelectPrimitive.ItemIndicator>
      </span>

      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
}

/**
 * SelectSeparator - Visual separator between items
 */
function SelectSeparator({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof SelectPrimitive.Separator> & {
  ref?: Ref<ComponentRef<typeof SelectPrimitive.Separator>>
}) {
  return (
    <SelectPrimitive.Separator
      ref={ref}
      className={cn('-mx-1 my-1 h-px bg-butter-gray-200', className)}
      {...props}
    />
  )
}

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
}
